import { ActionButton, Callout, DirectionalHint, Icon, IconButton, Link, mergeStyles, Stack } from "@fluentui/react";
import { FunctionComponent, useState } from "react";
import { LinkSrvSurvey } from "../../components/LinkSrvSurvey";
import { applyBodyType, applyBuffs, applyStrongLinkBoost, bodyIsTidalToStar } from "../../economy-model2";
import { store } from "../../local-storage";
import { Economy, getSiteType, mapName } from "../../site-data";
import { predictSurfaceSlots } from '../../slot-prediction';
import { AuditEconomy, BodyMap2, EconomyMap, SiteMap2 } from "../../system-model2";
import { appTheme, cn } from "../../theme";
import { BodyFeature, mapBodyFeature } from "../../types";
import { Bod, BT } from "../../types2";
import { asPosNegTxt2 } from "../../util";
import { mapBodyFeatureColor, mapBodyFeatureIcon } from "./SitesBodyView";
import { SystemView2 } from "./SystemView2";
import { ViewEditSlotCount } from "./ViewEditSlotCount";
import { EconomyBlock } from "../../components/EconomyBlock";

const cbt = mergeStyles({
  color: appTheme.semanticColors.bodyText
});

const borderLeft = '1px solid ' + appTheme.palette.purpleDark;
const thd = mergeStyles({
  padding: 4,
  textAlign: 'center',
});
const col1 = mergeStyles({
  padding: '4px 8px',
  textAlign: 'left',
  verticalAlign: 'top',
});
const col2 = mergeStyles({
  padding: '4px 0 4px 4px',
  textAlign: 'right',
  verticalAlign: 'top',
  borderLeft,
});
const col3 = mergeStyles({
  padding: 4,
  textAlign: 'left',
  verticalAlign: 'top',
});
const col4 = mergeStyles({
  padding: 4,
  textAlign: 'left',
  verticalAlign: 'top',
  borderLeft,
});
const col4b = mergeStyles({
  paddingBottom: 6
});


const calcForBody = (bod: Bod | BodyMap2, sysView: SystemView2) => {

  const mapColony = { agriculture: 0, extraction: 0, hightech: 0, industrial: 0, military: 0, refinery: 0, terraforming: 0, tourism: 0, service: 0, } as EconomyMap;
  const site: SiteMap2 = {
    buildType: 'no_truss',
    type: getSiteType('no_truss')!,
    status: 'complete',
    bodyNum: bod.num,
    body: bod as BodyMap2,
    sys: sysView.state.sysMap,
    id: '', buildId: '', name: '', original: undefined!, economyAudit: [], marketId: 0,
  };
  // run economy calculations as if it were a Coriolis
  applyBodyType(mapColony, site);
  applyBuffs(mapColony, site, false);
  const auditColony = site.economyAudit!.sort((a, b) => a.inf.localeCompare(b.inf));

  site.economyAudit = [];
  const mapLinks = { agriculture: 0, extraction: 0, hightech: 0, industrial: 0, military: 0, refinery: 0, terraforming: 0, tourism: 0, service: 0, } as EconomyMap;
  for (const econ in mapColony) {
    applyStrongLinkBoost(econ as Economy, mapLinks, site, '');
  }

  // and trim initial "+ boost: "
  const auditLinks = site.economyAudit;
  for (const a of auditLinks) { a.reason = a.reason.substring(10); }

  return { mapColony, auditColony, mapLinks, auditLinks };
};


const getEconomyTable = (map: EconomyMap, audit: AuditEconomy[], sumValues: boolean) => {
  const colonyRows: JSX.Element[] = [];
  let nn = 1;
  for (const econ in map) {
    const val = map[econ as keyof EconomyMap];
    if (val === 0) { continue; }

    colonyRows.push(<tr key={`cr-er-${++nn}`}><td className={cn.bt} colSpan={4} /><td /></tr>);

    const subAudit = audit.filter(a => a.inf === econ);
    for (let i = 0; i < subAudit.length; i++) {
      const d = subAudit[i];
      const isLast = i === subAudit.length - 1;
      colonyRows.push(<tr key={`cr-er-${++nn}`}>
        <td className={`${col1} ${isLast ? col4b : ''}`}>
          <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 4 }}>
            {i === 0 && <EconomyBlock economy={econ} size='10px' />}
            {i === 0 && <span>{mapName[econ]}</span>}
          </Stack>
        </td>
        <td className={`${col2}`}>{asPosNegTxt2(d.delta)}</td>
        <td className={`${col3}`}>{sumValues && isLast && ` = ${val.toFixed(2)}`}</td>
        <td className={`${col4} ${isLast ? col4b : ''}`}>{d.reason}</td>
      </tr>);
    }
  }

  // add a blank row with empty cells at the end
  colonyRows.push(<tr key={`cr-er-${++nn}`}><td className={`${col1}`} /><td className={`${col2}`} /><td className={`${col3}`} /><td className={`${col4}`} /></tr>);

  return <>
    <table cellPadding={0} cellSpacing={0} style={{ fontSize: 11, lineHeight: '8px' }}>
      <thead>
        <tr style={{ width: 70, fontSize: 10, textAlign: 'center', padding: 4 }}>
          <th className={thd}>Economy</th>
          <th className={thd} style={{ borderLeft }} colSpan={2}>Value</th>
          <th className={thd} style={{ borderLeft }}>Effects</th>
        </tr>
      </thead>
      <tbody style={{ color: appTheme.semanticColors.bodyText }}>
        {colonyRows}
      </tbody>
    </table>
  </>;
};

export const BodyCard: FunctionComponent<{ targetId: string, bod: Bod | BodyMap2, sysView: SystemView2, onClose: () => void }> = (props) => {
  const { bod, sysView } = props;
  const bodySlots = sysView.state.sysMap.slots[bod.num] ?? [-1, -1];
  const isLandable = bod.features.includes(BodyFeature.landable);
  const [data] = useState(calcForBody(bod, sysView));
  const [helpNoGroundSlots, setHelpNoGroundSlots] = useState(false);

  // only show the hint on bodies that could be landable
  const showMissingGroundSlotsHint = !isLandable && [BT.hmc, BT.ib, BT.mrb, BT.rb, BT.ri].includes(bod.type);
  const ignoreTidalLock = !bodyIsTidalToStar(sysView.state.sysMap, bod);

  return <div>
    <Callout
      target={`#${props.targetId}`}
      setInitialFocus
      alignTargetEdge
      directionalHint={DirectionalHint.rightTopEdge}
      gapSpace={-8}
      style={{
        border: '1px solid ' + appTheme.palette.themePrimary,
        padding: 0,
      }}
      styles={{
        beak: { backgroundColor: appTheme.palette.themePrimary },
        calloutMain: {
          boxShadow: `${appTheme.palette.blackTranslucent40} -1px 0px 20px 10px`,
        }
      }}
      role="dialog"
      onDismiss={() => props.onClose()}
    >
      <div className='system-view2' style={{ position: 'relative', padding: 10, textTransform: 'capitalize', cursor: 'default' }}>

        <h3 className={cn.h3} style={{ fontSize: 20 }}>{bod.name}</h3>

        <div style={{ color: appTheme.palette.themePrimary }}>
          {!!store.cmdrName && <IconButton
            className={cn.ibBri}
            iconProps={{ iconName: 'CircleAddition' }}
            title={`Add a new site to: ${bod.name}`}
            style={{ position: 'absolute', right: 20, top: 42, paddingTop: 2 }}
            onClick={() => {
              sysView.createNewSite(bod.num);
            }}
          />}

          <div style={{ fontSize: 10 }}>
            {bod.subType}
            <span style={{ color: 'grey', margin: '0 4px' }}>-</span>
            <span style={{ color: appTheme.palette.themeTertiary }}>Arrival:</span> ~{bod.distLS.toFixed(1)} ls
          </div>
          <div style={{ color: appTheme.palette.themeTertiary, fontSize: 10 }}>
            Surface temp:&nbsp;<span style={{ color: appTheme.palette.themeSecondary, marginLeft: 4 }}>{bod.temp.toFixed(1)} K</span>
            <span style={{ color: 'grey', margin: '0 4px' }}>-</span>
            Gravity: <span style={{ color: appTheme.palette.themeSecondary }}>{bod.gravity.toFixed(2)} g</span>
          </div>

          <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }} style={{ marginTop: 10 }}>
            <div>Slots:</div>

            <ViewEditSlotCount
              showIcon bright
              max={bodySlots[0]}
              current={0}
              isOrbital={true}
              isPredicted={false}
              onChange={newCount => sysView.setBodySlot(bod.num, newCount, true)}
            />

            {isLandable && <ViewEditSlotCount
              showIcon bright
              max={bodySlots[1] < 0 ? predictSurfaceSlots(bod) : bodySlots[1]}
              current={0}
              isOrbital={false}
              isPredicted={bodySlots[1] < 0}
              onChange={newCount => sysView.setBodySlot(bod.num, newCount, false)}
            />}

            {showMissingGroundSlotsHint && <Link
              id='missing-ground-slots'
              style={{ marginLeft: 40, color: appTheme.palette.themeTertiary, fontSize: 12, position: 'relative' }}
              onClick={() => setHelpNoGroundSlots(v => !v)}
            >
              Missing ground slots?
            </Link>}
          </Stack>

          {helpNoGroundSlots && <Callout
            target='#missing-ground-slots'
            styles={{
              beak: { backgroundColor: appTheme.palette.neutralTertiaryAlt, },
              calloutMain: {
                backgroundColor: appTheme.palette.neutralTertiaryAlt,
                color: appTheme.palette.neutralDark,
              }
            }}
            onDismiss={() => setHelpNoGroundSlots(false)}
          >
            If the game shows ground slots but there is no button, that suggests<br />
            this body was last scanned before Odyssey was released.
            <br /><br />
            <b>To fix:</b>
            <ul style={{ margin: 0 }}>
              <li>With an EDDN client running, re-scan the body or FSS this system</li>

              <li>
                Check this body shows as Landable on&nbsp;
                <LinkSrvSurvey href={`https://spansh.co.uk/system/${props.sysView.state.sysMap.id64}#system-bodies`} text='Spansh' />
              </li>

              <li>Then <ActionButton className={cn.bBox2} style={{ height: 24, fontSize: 12 }} text='Re-import bodies' onClick={() => {
                props.sysView.doImport('bodies');
                setHelpNoGroundSlots(false);
              }} /></li>
            </ul>
          </Callout>}

          {bod.features.length > 0 && <Stack horizontal wrap tokens={{ childrenGap: '0 8px' }} style={{ marginTop: 10 }}>
            <div>Features:</div>
            <Stack className={cbt} horizontal wrap verticalAlign='center' tokens={{ childrenGap: '0 8px' }} style={{ maxWidth: 300 }}>

              {bod.features.map(f => {
                return <div key={f} style={{ marginLeft: 4 }} title={ignoreTidalLock ? 'Tidal lock penalty will not apply' : undefined}>
                  <Icon
                    className='icon-inline'
                    key={`bfi-${bod.num}${f}`}
                    title={mapBodyFeature[f]}
                    iconName={mapBodyFeatureIcon[f]}
                    style={{ color: mapBodyFeatureColor[f].slice(0, -1) + ', 0.8)' }}
                  />
                  <span style={{
                    marginLeft: 6,
                    color: f === BodyFeature.tidal && ignoreTidalLock ? 'grey' : undefined,
                    textDecoration: f === BodyFeature.tidal && ignoreTidalLock ? 'line-through' : undefined
                  }}>{mapBodyFeature[f]}</span>
                </div>;
              })}
            </Stack>
          </Stack>}


          <div style={{ marginTop: 10, textTransform: 'none' }}>
            <div>Default Economies:</div>
            <div style={{ color: appTheme.palette.themeTertiary, fontSize: 10 }}>
              Ports with a colony economy will start with the following:
            </div>
            {getEconomyTable(data.mapColony, data.auditColony, true)}
          </div>


          <div style={{ marginTop: 10, textTransform: 'none' }}>
            <div>Strong link boosts:</div>
            <div style={{ color: appTheme.palette.themeTertiary, fontSize: 10 }}>
              Each strong link will receive the following boosts:
            </div>
            {getEconomyTable(data.mapLinks, data.auditLinks, false)}
          </div>


        </div>
      </div>
    </Callout>
  </div >;
}
